import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, FileText, MapPin, Package } from 'lucide-react';
import type { Asset, AssetStatus } from '@/types/asset';
import type { Category, Location, SubCategory, SubLocation } from '@/types/settings';
import type { SubLedger } from '@/types/financials';
import { useState, useEffect } from 'react';
import { dataService } from '@/services/data-service';
import { financialsService } from '@/services/financials-graphql-service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { AssetDetailPhotos } from './AssetDetailPhotos';
import type { AssetPhoto } from './AssetDetailPhotos';
import { BarcodeQRCodeSection } from '../barcode-qrcode-section';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import { format } from 'date-fns';

interface FetchState<T> {
  data: T[];
  loading: boolean;
  error: boolean;
}

function useFetchData<T>(
  fetchFn: () => Promise<T[]>,
  validateFn: (item: T) => boolean,
  logPrefix: string
): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: [],
    loading: false,
    error: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      setState(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await fetchFn();
        const invalidItems = data.filter(item => !validateFn(item));
        if (invalidItems.length > 0) {
          console.warn(`[${logPrefix}] Found items with empty values:`, invalidItems);
        }
        setState({ data, loading: false, error: false });
      } catch (error) {
        console.error(`[${logPrefix}] Failed to fetch:`, error);
        setState({ data: [], loading: false, error: true });
      }
    };
    fetchData();
  }, []);

  return state;
}

interface TextFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly isEditMode: boolean;
  readonly required?: boolean;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly type?: string;
  readonly min?: string;
}

function TextField({ 
  id, label, value, onChange, placeholder, isEditMode, required, error, disabled, type = 'text', min 
}: Readonly<TextFieldProps>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {isEditMode ? (
        <Input
          id={id}
          type={type}
          min={min}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={error ? 'border-destructive' : ''}
        />
      ) : (
        <Input
          id={id}
          value={value}
          readOnly
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

interface SelectFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string | undefined;
  readonly onValueChange: (value: string) => void;
  readonly isEditMode: boolean;
  readonly placeholder?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly displayValue?: string;
  readonly children: React.ReactNode;
}

function SelectField({
  id,
  label,
  value,
  onValueChange,
  isEditMode,
  placeholder,
  error,
  required = false,
  disabled = false,
  displayValue,
  children
}: Readonly<SelectFieldProps>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {isEditMode ? (
        <Select
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
        >
          <SelectTrigger id={id} className={error ? 'border-destructive' : ''}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {children}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          value={displayValue ?? value ?? ''}
          readOnly
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

interface SelectDropdownContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly emptyMessage: string;
  readonly errorMessage: string;
  readonly children: React.ReactNode;
}

function SelectDropdownContent({ 
  loading, error, emptyMessage, errorMessage, children 
}: Readonly<SelectDropdownContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="p-2 text-sm text-destructive">{errorMessage}</div>;
  }
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return <div className="p-2 text-sm text-muted-foreground">{emptyMessage}</div>;
  }
  return <>{children}</>;
}

interface CategorySelectContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly categories: Category[];
}

function CategorySelectContent({ loading, error, categories }: Readonly<CategorySelectContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-2 text-sm text-destructive">
        Failed to load categories. Please check your connection or contact support.
      </div>
    );
  }
  if (categories.length === 0) {
    return (
      <div className="p-2 text-sm text-muted-foreground">
        No categories available. Please add categories in Settings.
      </div>
    );
  }
  return (
    <>
      {categories
        .filter((cat) => {
          const value = cat.description ?? cat.code;
          return value && typeof value === 'string' && value.trim().length > 0;
        })
        .map((cat) => {
          const value = (cat.description ?? cat.code).trim();
          return (
            <SelectItem key={cat.id} value={value}>
              {cat.description ?? cat.code}
            </SelectItem>
          );
        })}
    </>
  );
}

interface LocationSelectContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly locations: Location[];
}

function LocationSelectContent({ loading, error, locations }: Readonly<LocationSelectContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-2 text-sm text-destructive">
        Failed to load locations. Please check your connection or contact support.
      </div>
    );
  }
  if (locations.length === 0) {
    return (
      <div className="p-2 text-sm text-muted-foreground">
        No locations available. Please add locations in Settings.
      </div>
    );
  }
  return (
    <>
      {locations
        .filter((loc) => {
          const value = loc.name;
          return value && typeof value === 'string' && value.trim().length > 0;
        })
        .map((loc) => (
          <SelectItem key={loc.id} value={loc.name.trim()}>
            {loc.name}
          </SelectItem>
        ))}
    </>
  );
}

interface SubLedgerSelectContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly subLedgers: SubLedger[];
}

function SubLedgerSelectContent({ loading, error, subLedgers }: Readonly<SubLedgerSelectContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="p-2 text-sm text-muted-foreground">Failed to load sub ledgers</div>;
  }
  return (
    <>
      <SelectItem value="none">None</SelectItem>
      {subLedgers
        .filter((ledger) => {
          const value = ledger.code;
          return value && typeof value === 'string' && value.trim().length > 0;
        })
        .map((ledger) => (
          <SelectItem key={ledger.id} value={ledger.code.trim()}>
            {ledger.code} - {ledger.description}
          </SelectItem>
        ))}
    </>
  );
}

interface SubCategorySelectContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly subCategories: SubCategory[];
}

function SubCategorySelectContent({ loading, error, subCategories }: Readonly<SubCategorySelectContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="p-2 text-sm text-muted-foreground">Failed to load sub categories</div>;
  }
  return (
    <>
      <SelectItem value="none">None</SelectItem>
      {subCategories
        .filter((subCat) => {
          const value = subCat.name;
          return value && typeof value === 'string' && value.trim().length > 0;
        })
        .map((subCat) => (
          <SelectItem key={subCat.id} value={subCat.name.trim()}>
            {subCat.name}
          </SelectItem>
        ))}
    </>
  );
}

interface SubLocationSelectContentProps {
  readonly loading: boolean;
  readonly error: boolean;
  readonly subLocations: SubLocation[];
}

function SubLocationSelectContent({ loading, error, subLocations }: Readonly<SubLocationSelectContentProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="p-2 text-sm text-muted-foreground">Failed to load sub locations</div>;
  }
  return (
    <>
      <SelectItem value="none">None</SelectItem>
      {subLocations
        .filter((subLoc) => {
          const value = subLoc.name;
          return value && typeof value === 'string' && value.trim().length > 0;
        })
        .map((subLoc) => (
          <SelectItem key={subLoc.id} value={subLoc.name.trim()}>
            {subLoc.name}
          </SelectItem>
        ))}
    </>
  );
}

interface AssetDetailOverviewProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
  readonly isCreateMode?: boolean;
  readonly errors?: Record<string, string>;
  readonly systemOptions?: any;
}

const validateCategory = (cat: Category) => {
  const value = cat.description ?? cat.code;
  return Boolean(value && value.trim().length > 0);
};

const validateLocation = (loc: Location) => {
  const value = loc.name;
  return Boolean(value && value.trim().length > 0);
};

const validateSubLedger = (ledger: SubLedger) => {
  const value = ledger.code;
  return Boolean(value && value.trim().length > 0);
};

function applyCategoryDefaults(
  asset: Asset,
  categoryValue: string,
  categories: Category[],
  systemOptions: any
): Partial<Asset> {
  const selectedCategory = categories.find(c => (c.description ?? c.code) === categoryValue);
  if (!selectedCategory) {
    return {};
  }
  return {
    depreciationMethod: (selectedCategory.defaultMethod ?? systemOptions?.depreciation?.defaultMethod) ?? 'Straight Line',
    depreciationPercentage: (selectedCategory.defaultPercentage ?? systemOptions?.depreciation?.defaultPercentage) ?? 0,
    depreciationYears: (selectedCategory.defaultPeriodYears ?? systemOptions?.depreciation?.defaultYears) ?? 0,
    postingFrequency: (selectedCategory.defaultFrequency ?? systemOptions?.depreciation?.defaultPostingFrequency) ?? 'Annually',
  };
}

export function AssetDetailOverview({
  asset, 
  onAssetUpdate, 
  isEditMode = false, 
  isCreateMode = false,
  errors = {},
  systemOptions
}: Readonly<AssetDetailOverviewProps>) {
  const categoriesState = useFetchData<Category>(
    () => dataService.getCategories(),
    validateCategory,
    'AssetDetailOverview Categories'
  );
  
  const locationsState = useFetchData<Location>(
    () => dataService.getLocations(),
    validateLocation,
    'AssetDetailOverview Locations'
  );
  
  const subLedgersState = useFetchData<SubLedger>(
    () => financialsService.getSubLedgers(),
    validateSubLedger,
    'AssetDetailOverview SubLedgers'
  );

  const categories = categoriesState.data;
  const categoriesLoading = categoriesState.loading;
  const categoriesError = categoriesState.error;
  
  const locations = locationsState.data;
  const locationsLoading = locationsState.loading;
  const locationsError = locationsState.error;
  
  const subLedgers = subLedgersState.data;
  const subLedgersLoading = subLedgersState.loading;
  const subLedgersError = subLedgersState.error;

  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [subCategoriesError, setSubCategoriesError] = useState(false);

  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [subLocationsLoading, setSubLocationsLoading] = useState(false);
  const [subLocationsError, setSubLocationsError] = useState(false);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!asset.category || !categories.length) {
        setSubCategories([]);
        return;
      }
      const selectedCategory = categories.find(c => c.code === asset.category || c.description === asset.category);
      if (!selectedCategory) {
        setSubCategories([]);
        return;
      }
      setSubCategoriesLoading(true);
      setSubCategoriesError(false);
      try {
        const data = await dataService.getSubCategoriesByParentId(selectedCategory.id);
        setSubCategories(data);
      } catch (error) {
        console.error('[AssetDetailOverview] Failed to fetch sub categories:', error);
        setSubCategoriesError(true);
        setSubCategories([]);
      } finally {
        setSubCategoriesLoading(false);
      }
    };
    fetchSubCategories();
  }, [asset.category, categories]);

  useEffect(() => {
    const fetchSubLocations = async () => {
      if (!asset.location || !locations.length) {
        setSubLocations([]);
        return;
      }
      const selectedLocation = locations.find(l => l.name === asset.location || l.code === asset.location);
      if (!selectedLocation) {
        setSubLocations([]);
        return;
      }
      setSubLocationsLoading(true);
      setSubLocationsError(false);
      try {
        const data = await dataService.getSubLocationsByParentId(selectedLocation.id);
        setSubLocations(data);
      } catch (error) {
        console.error('[AssetDetailOverview] Failed to fetch sub locations:', error);
        setSubLocationsError(true);
        setSubLocations([]);
      } finally {
        setSubLocationsLoading(false);
      }
    };
    fetchSubLocations();
  }, [asset.location, locations]);

  const handleFieldChange = (field: keyof Asset, value: any) => {
    // Convert "none" to empty string for subLedger, subCategory, and subLocation
    const actualValue = (field === 'subLedger' || field === 'subCategory' || field === 'subLocation') && value === 'none' ? '' : value;
    let updatedAsset = { ...asset, [field]: actualValue };
    
    // Apply category-specific defaults when category changes (in both create and edit mode)
    if (field === 'category') {
      const categoryDefaults = applyCategoryDefaults(asset, value, categories, systemOptions);
      updatedAsset = { ...updatedAsset, ...categoryDefaults };
      // Clear subCategory when category changes
      updatedAsset = { ...updatedAsset, subCategory: undefined };
    }
    
    // Clear subLocation when location changes
    if (field === 'location') {
      updatedAsset = { ...updatedAsset, subLocation: undefined };
    }
    
    onAssetUpdate(updatedAsset);
  };

  const assetStatuses: { value: AssetStatus; label: string; description: string }[] = [
    { value: 'inService', label: 'In Service', description: 'Asset is currently in use' },
    { value: 'outOfService', label: 'Out of Service', description: 'Temporarily not in use but still owned' },
    { value: 'disposed', label: 'Disposed', description: 'Asset has been sold, scrapped, or written off' },
    { value: 'fullyDepreciated', label: 'Fully Depreciated', description: 'Asset has reached end of useful life for accounting' },
    { value: 'pending', label: 'Pending / On Order', description: 'Asset has been purchased but not yet received' },
    { value: 'underMaintenance', label: 'Under Maintenance / Repair', description: 'Temporarily out of service for repairs' },
    { value: 'reserved', label: 'Reserved', description: 'Allocated for a specific purpose or project' },
    { value: 'lostMissing', label: 'Lost / Missing', description: 'Cannot be located' },
    { value: 'leased', label: 'Leased', description: 'Asset is leased rather than owned outright' },
  ];

  // Check if asset code should be auto-generated
  const isAutoNumbered = isCreateMode && systemOptions?.autoNumbering?.enabled;
  const isAssetUsed = !isCreateMode && asset.used;

  return (
    <>
      {Object.keys(errors).length > 0 && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please fix the following errors: {Object.values(errors).join(', ')}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Main Details (2/3 width on XL screens) */}
        <div className="xl:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Basic Information
              </CardTitle>
              <CardDescription className="text-xs">
                Core asset identification and classification details
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Code */}
              <TextField
                id="code"
                label="Code"
                value={asset.code}
                onChange={(value) => handleFieldChange('code', value)}
                placeholder={isAutoNumbered ? 'Auto-generated on save' : 'Enter code'}
                isEditMode={isEditMode}
                required
                error={errors.code}
                disabled={isAutoNumbered || isAssetUsed}
              />

              {/* Description */}
              <TextField
                id="name"
                label="Description"
                value={asset.name}
                onChange={(value) => handleFieldChange('name', value)}
                placeholder="Enter asset description"
                isEditMode={isEditMode}
                required
                error={errors.name}
              />

              {/* Category */}
              <SelectField
                id="category"
                label="Category"
                value={asset.category ?? undefined}
                onValueChange={(value: string) => handleFieldChange('category', value)}
                isEditMode={isEditMode}
                placeholder="Select category"
                error={errors.category}
                required
                disabled={categoriesLoading}
                displayValue={asset.category}
              >
                <CategorySelectContent
                  loading={categoriesLoading}
                  error={categoriesError}
                  categories={categories}
                />
              </SelectField>

              {/* Sub Category */}
              <SelectField
                id="subCategory"
                label="Sub Category"
                value={asset.subCategory ?? 'none'}
                onValueChange={(value: string) => handleFieldChange('subCategory', value)}
                isEditMode={isEditMode}
                placeholder={!asset.category ? 'Select category first' : 'Select sub category'}
                disabled={!asset.category || subCategoriesLoading}
                displayValue={asset.subCategory ?? ''}
              >
                <SubCategorySelectContent
                  loading={subCategoriesLoading}
                  error={subCategoriesError}
                  subCategories={subCategories}
                />
              </SelectField>

              {/* Status */}
              <SelectField
                id="status"
                label="Status"
                value={asset.status ?? undefined}
                onValueChange={(value: string) => handleFieldChange('status', value as AssetStatus)}
                isEditMode={isEditMode}
                displayValue={assetStatuses.find(s => s.value === asset.status)?.label ?? asset.status}
              >
                {assetStatuses.map((status) => (
                  <SelectItem key={status.value} value={status.value} textValue={status.label}>
                    <div className="flex flex-col">
                      <span className="font-medium">{status.label}</span>
                      <span className="text-xs text-muted-foreground">{status.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectField>

              {/* Date Acquired */}
              <div className="space-y-2">
                <Label htmlFor="acquisitionDate">Date Acquired</Label>
                {isEditMode ? (
                  <DateInputPicker
                    date={asset.acquisitionDate ? new Date(asset.acquisitionDate) : undefined}
                    onDateChange={(date) => handleFieldChange('acquisitionDate', date ? format(date, 'yyyy-MM-dd') : '')}
                    placeholder="DD/MM/YYYY"
                  />
                ) : (
                  <Input
                    id="acquisitionDate"
                    value={asset.acquisitionDate ? new Date(asset.acquisitionDate).toLocaleDateString('en-GB') : ''}
                    readOnly
                  />
                )}
              </div>

              {/* Quantity */}
              <TextField
                id="quantity"
                label="Quantity"
                value={String(asset.quantity ?? 1)}
                onChange={(value) => handleFieldChange('quantity', parseInt(value) ?? 1)}
                isEditMode={isEditMode}
                type="number"
                min="1"
              />
            </CardContent>
          </Card>

          {/* Location & Assignment */}
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location & Assignment
              </CardTitle>
              <CardDescription className="text-xs">
                Physical location and ownership responsibility tracking
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Location */}
              <SelectField
                id="location"
                label="Location"
                value={asset.location ?? undefined}
                onValueChange={(value: string) => handleFieldChange('location', value)}
                isEditMode={isEditMode}
                placeholder="Select location"
                error={errors.location}
                required
                disabled={locationsLoading}
                displayValue={asset.location}
              >
                <LocationSelectContent
                  loading={locationsLoading}
                  error={locationsError}
                  locations={locations}
                />
              </SelectField>

              {/* Sub Location */}
              <SelectField
                id="subLocation"
                label="Sub Location"
                value={asset.subLocation ?? 'none'}
                onValueChange={(value: string) => handleFieldChange('subLocation', value)}
                isEditMode={isEditMode}
                placeholder={!asset.location ? 'Select location first' : 'Select sub location'}
                disabled={!asset.location || subLocationsLoading}
                displayValue={asset.subLocation ?? ''}
              >
                <SubLocationSelectContent
                  loading={subLocationsLoading}
                  error={subLocationsError}
                  subLocations={subLocations}
                />
              </SelectField>

              {/* Assigned To (Owner) */}
              <TextField
                id="assignedTo"
                label="Assigned To"
                value={asset.assignedTo ?? ''}
                onChange={(value) => handleFieldChange('assignedTo', value)}
                isEditMode={isEditMode}
                placeholder="Enter assigned person"
              />

              {/* Resource Code */}
              <TextField
                id="resourceCode"
                label="Resource Code"
                value={asset.resourceCode ?? ''}
                onChange={(value) => handleFieldChange('resourceCode', value)}
                isEditMode={isEditMode}
                placeholder="Enter resource code"
              />

              {/* Sub Ledger */}
              <SelectField
                id="subLedger"
                label="Sub Ledger"
                value={asset.subLedger ?? 'none'}
                onValueChange={(value: string) => handleFieldChange('subLedger', value)}
                isEditMode={isEditMode}
                placeholder="Select sub ledger"
                disabled={subLedgersLoading}
                displayValue={asset.subLedger ?? ''}
              >
                <SubLedgerSelectContent
                  loading={subLedgersLoading}
                  error={subLedgersError}
                  subLedgers={subLedgers}
                />
              </SelectField>
            </CardContent>
          </Card>

          {/* Manufacturer & Model */}
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Manufacturer & Model Details
              </CardTitle>
              <CardDescription className="text-xs">
                Product information and serial number tracking
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Manufacturer */}
              <TextField
                id="manufacturer"
                label="Manufacturer"
                value={asset.manufacturer ?? ''}
                onChange={(value) => handleFieldChange('manufacturer', value)}
                isEditMode={isEditMode}
                placeholder="Enter manufacturer"
              />

              {/* Model */}
              <TextField
                id="model"
                label="Model"
                value={asset.model ?? ''}
                onChange={(value) => handleFieldChange('model', value)}
                isEditMode={isEditMode}
                placeholder="Enter model"
              />

              {/* Serial Number */}
              <TextField
                id="serialNumber"
                label="Serial Number"
                value={asset.serialNumber ?? ''}
                onChange={(value) => handleFieldChange('serialNumber', value)}
                isEditMode={isEditMode}
                placeholder="Enter serial number"
              />
            </CardContent>
          </Card>

        </div>

        {/* Right Column - Photos & Barcode (1/3 width on XL screens, full width below) */}
        <div className="xl:col-span-1 space-y-6">
          <AssetDetailPhotos
            photos={asset.photos ?? []}
            onChange={(photos: AssetPhoto[]) => handleFieldChange('photos', photos)}
            disabled={!isEditMode}
            maxPhotos={5}
          />

          {/* Barcode / QR Code */}
          <div className="border border-border/40 rounded-lg">
            <BarcodeQRCodeSection
              barcodeType={asset.barcodeType as 'barcode' | 'qrcode' | undefined}
              barcodeValue={asset.barcodeValue}
              onCodeChange={(barcodeType, barcodeValue) => {
                onAssetUpdate({
                  ...asset,
                  barcodeType: barcodeType as any,
                  barcodeValue: barcodeValue
                });
              }}
              disabled={!isEditMode}
              locationCode={locations.find(l => l.name === asset.location)?.code}
              assetNumericId={asset.id}
            />
          </div>
        </div>
      </div>
    </>
  );
}
